import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { authService } from "./authService";
import { authStorage } from "./authStorage";
import { deriveAuthSession } from "./authUtils";
import type { AuthState, AuthUser, LoginCredentials } from "./types";

const storedUser = authStorage.getAuth();

const initialState: AuthState = {
  ...deriveAuthSession(storedUser, authStorage.getToken()),
  loading: false,
  error: null,
};

export const login = createAsyncThunk<AuthUser, LoginCredentials, { rejectValue: string }>(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      return await authService.login({ userName: username, password });
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Login failed");
    }
  }
);

export const logout = createAsyncThunk<null, void, { rejectValue: string }>(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      authStorage.clear();
      return null;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "Logout failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    syncFromStorage: (state) => {
      Object.assign(state, deriveAuthSession(authStorage.getAuth(), authStorage.getToken()));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthUser>) => {
        state.loading = false;
        state.error = null;
        Object.assign(state, deriveAuthSession(action.payload, action.payload.token));
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? "Login failed";
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        Object.assign(state, deriveAuthSession(null, null));
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? "Logout failed";
      });
  },
});

export const { clearError, syncFromStorage } = authSlice.actions;
export default authSlice.reducer;
